import { BrowserRouter as Router, useLocation } from 'react-router-dom'
import { NoPauseToaster } from './components/NoPauseToaster'
import { useEffect } from 'react'
import AppRoutes from './routes'
import { isApp, isIOS } from './utils/platform'
import { acquireNoKeyboardResize } from './utils/flutterStatusBar'
import { setAppImmersive } from './utils/flutterStatusBar'
import { TOAST_DURATION } from './utils/toast'
import { useCategoryStore } from './stores/categoryIconStore'
import { useAuthStore } from './stores/authStore'
import { useReviewStore } from './stores/reviewStore'
import {
  setupFcmBridge,
  registerFcm,
  requestNotificationPermissionOnce,
} from './utils/fcmRegister'
import UpdateRequiredGate from './components/UpdateRequiredGate'
import { usePushOpenReport } from './hooks/usePushOpenReport'
import { reportInstallReferrer } from './utils/installReferrer'

/** 훅은 Router 안에서만 쓸 수 있어서 껍데기 하나를 둔다 */
const PushOpenReporter = () => {
  usePushOpenReport()
  return null
}

/**
 * '기록할 사진 → 직접 찾기' 로 **가게보다 먼저 심어 둔 사진**을 지키고, 버린다.
 *
 * 그 길은 어느 가게인지 모르는 채로 사진만 전역에 심는다(Collect.tsx
 * startSearch). 가게를 고르면 기록 화면이 집어 가는데, 고르다 말고 딴 데로
 * 가면 심어 둔 채로 남아 **나중에 전혀 다른 가게에 따라붙는다.**
 *
 * 전에는 검색 화면이 떠날 때 버렸는데, 가는 곳이 `/store-review` 인지만 봤다.
 * 검색 결과를 누르면 `/main-map?placeId=` 로 가므로(Search.tsx handlePlaceClick)
 * **지도로 넘어가는 순간 사진이 통째로 지워졌다.** 가게를 고르는 길은 화면
 * 하나가 아니라 여러 칸짜리 여정이라, 버릴지 말지는 그 여정 전체를 아는
 * 자리에서 판단해야 한다.
 *
 * 그래서 여기서 **주소만** 본다 — 여정 안이면 두고, 벗어나면 버린다.
 * 가게가 정해지는 순간 `storeId` 가 채워져 이 판단은 더 이상 돌지 않는다.
 */
const STAGED_PHOTO_PATHS = [
  '/collect',
  '/search',
  '/main-map',
  '/store-review',
  '/photo-edit',
]

const StagedPhotoGuard = () => {
  const { pathname } = useLocation()
  useEffect(() => {
    const rs = useReviewStore.getState()
    if (rs.storeId !== null || rs.photos.length === 0) return
    if (STAGED_PHOTO_PATHS.some((p) => pathname.startsWith(p))) return
    rs.resetReviewData()
  }, [pathname])
  return null
}

function App() {
  const loadCategories = useCategoryStore((state) => state.loadCategories)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  // FCM: 네이티브 토큰 수신 핸들러는 인증 여부와 무관하게 상시 켜둔다
  // (앱 시작 직후 네이티브가 토큰을 보내도 누락되지 않도록)
  useEffect(() => {
    setupFcmBridge()
  }, [])

  /*
   * 키보드가 떠도 앱이 웹뷰 크기를 바꾸지 않게 한다. 앱이 뜰 때 한 번만 끈다.
   *
   * 왜 — 실기기 20회 측정
   * -------------------
   * 창 크기가 바뀔 때 한 프레임 잘못된 값이 오고, 그때 화면이 크게 밀린다.
   *   정상: iH874 → 820 → 757 → … → 494
   *   밀림: iH874 → 494 → 843 → 757 → … → 494
   * 창이 874로 고정됐던 4회만 정상이었다. 창을 안 바꾸면 잘못 올 값이 없다.
   *
   * 화면마다 껐다 켜는 방식은 실패했다. 앱이 요청을 처리하기 전에 사용자가
   * 먼저 입력칸을 탭해버려서, 20회 중 한 화면에 오래 머문 4회만 적용됐다.
   * 그래서 시작할 때 한 번 끄고 계속 꺼둔다 — 타이밍 문제가 사라진다.
   *
   * 되돌리지 않는다. 화면별 useNoKeyboardResize는 카운터를 함께 쓰므로
   * 여기서 하나를 계속 쥐고 있으면 중간에 켜지는 일이 없다.
   *
   * 대신 입력칸이 키보드에 가리지 않도록 각 화면이 자리를 잡아야 한다.
   * 전체 화면 폼들은 웹킷이 알아서 스크롤해주고(이미 이 방식으로 동작 중),
   * 자세히보기 댓글칸은 useKeyboardInset이 키보드 높이만큼 비운다.
   */
  useEffect(() => {
    if (isApp()) acquireNoKeyboardResize()
  }, [])

  // 인증되면(앱 시작·로그인) 어느 화면이든 토큰을 등록한다
  // (기존엔 MainHome에서만 등록 → 온보딩 거치는 신규 유저는 누락됐음)
  useEffect(() => {
    if (isAuthenticated) registerFcm()
  }, [isAuthenticated])

  // 권한 요청도 같은 자리에서 한다. 홈을 거치지 않는 진입(딥링크·사진 공유)
  // 에서는 묻지도 않고 지나가던 것을 막는다. 자세한 사정은 함수 주석 참고.
  useEffect(() => {
    if (isAuthenticated) requestNotificationPermissionOnce()
  }, [isAuthenticated])

  // 안드로이드 설치 경로를 계정에 붙인다. 값이 없으면 아무 일도 하지 않는다
  useEffect(() => {
    if (isAuthenticated) reportInstallReferrer()
  }, [isAuthenticated])

  useEffect(() => {
    // 앱 환경일 때 body에 클래스 추가 및 웹뷰 확대 방지 (지도 영역은 Naver Map이 자체 줌 처리)
    if (isApp()) {
      document.body.classList.add('app-env')
      // iOS 앱만 하단 edge-to-edge 보정 대상 (Flutter가 iOS만 SafeArea bottom 해제).
      // 안드로이드는 env(safe-area-inset-bottom) 값이 달라 넣으면 레이아웃 깨져서 제외.
      if (isIOS()) {
        document.body.classList.add('ios-app')
      }
      const meta = document.querySelector('meta[name="viewport"]')
      if (meta) {
        // viewport-fit=cover 유지: 빼면 iOS에서 env(safe-area-inset-*)이 0이 되어
        // 지도 화면이 상태바(헤더) 영역까지 깔리지 않음
        meta.setAttribute(
          'content',
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
        )
      }

      // 상시 immersive: 웹뷰를 항상 노치 뒤까지 깔아 지오메트리를 고정한다.
      // → 시트 열림/닫힘에 네이티브 리레이아웃이 없어 깜빡임이 사라짐.
      //   콘텐츠 보정 클래스(immersive-pad)는 라우트별로 AppRoutes에서 토글한다(지도만 예외).
      setAppImmersive(true)
    } else {
      document.body.classList.remove('app-env')
    }

    // 카테고리 정보 초기화
    loadCategories()
  }, [loadCategories])

  return (
    <Router>
      {/*
        푸시 열람 보고는 **게이트 바깥**에 둔다.

        안쪽(AppRoutes)에 두면 업데이트 안내에 막힌 사용자는 라우터 자체가
        마운트되지 않아 보고가 돌지 않는다. 그 사람들도 알림을 누른 것은
        맞으므로 열람으로 세야 한다. 실제로 10/18 발송분이 그래서 0.7%로
        찍혔다 — 최소 버전에 못 미치는 사용자가 전체의 대부분이었다.
      */}
      <PushOpenReporter />
      <StagedPhotoGuard />

      {/* 너무 오래된 앱이면 이 안쪽을 업데이트 안내 화면으로 덮는다 */}
      <UpdateRequiredGate>
        <AppRoutes />
      </UpdateRequiredGate>
      <NoPauseToaster
        position="bottom-center"
        containerStyle={{
          bottom: 100,
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: '390px',
          width: '100%',
        }}
        toastOptions={{
          duration: TOAST_DURATION,
        }}
      />
    </Router>
  )
}

export default App
